// Game rules the server enforces. Plain JavaScript so Edge Functions (Deno) and Node tests share it.

/** Parse the Phantom sign-in message the client signs (see src/wallet.js). */
export function parseLoginMessage(msg) {
  const lines = String(msg || '').split('\n');
  if (lines[0] !== 'Sol Clans sign-in') return null;
  const get = (k) => { const l = lines.find((x) => x.startsWith(k + ': ')); return l ? l.slice(k.length + 2) : null; };
  const wallet = get('Wallet'), nonce = get('Nonce'), issued = Date.parse(get('Issued') || '');
  if (!wallet || !nonce || !/^[A-Za-z0-9]{8,64}$/.test(nonce) || !Number.isFinite(issued)) return null;
  return { wallet, nonce, issued, domain: get('Domain') };
}
export const LOGIN_MAX_AGE_MS = 5 * 60e3;

/** How much a base's Sol Hall level is worth in trophies: Sol Hall 1 ≈ 0.58×, 6 = 1×, 12 = 1.5×, 17 ≈ 1.92×.
 *  Beating a stronger base pays more, so low-level players can't farm each other to the top of the leaderboard. */
export const coreScale = (core) => 0.5 + Math.max(1, Math.min(17, core | 0 || 1)) / 12;

/** Trophy change for a raid on a real player (the game imports this same function).
 *  Attacker: gains on 2 or 3 stars (more for a higher defender Sol Hall), loses under 50% destruction, otherwise
 *  (1 star at 50%+) no change. Defender: loses when the attacker did more than 50%, gains when the attack stayed under 50%. */
export function raidTrophies(stars, attackerTrophies, defenderTrophies, pct, defenderCore = 6) {
  const s = Math.max(0, Math.min(3, stars | 0)), p = Math.max(0, Math.min(100, pct | 0));
  const gap = Math.max(-10, Math.min(10, Math.round((defenderTrophies - attackerTrophies) / 100)));
  const win = Math.max(1, Math.round((6 + s * 7 + gap * 3) * coreScale(defenderCore)));
  const attacker = s >= 2 ? win : p < 50 ? -Math.max(4, 8 - gap) : 0;
  const defender = p > 50 ? -Math.min(40, Math.max(8, attacker)) : p < 50 ? Math.max(2, 4 - gap) : 0;
  return { attacker, defender };
}

/** Trophies for a practice base: half of what the same result against a real player of that Sol Hall (and equal
 *  trophies) would give. Practice bases are generated around the attacker's own Sol Hall, so that level is used.
 *  3 stars: 8 at Sol Hall 1 up to 26 at 17; under 50% loses half as much (−4); 1 star at 50%+ no change. */
export function practiceTrophies(stars, pct, attackerCore) {
  const c = Math.max(1, Math.min(17, attackerCore | 0 || 1));
  return Math.round(raidTrophies(stars, 0, 0, pct, c).attacker / 2);
}

/** Loot from a defender's published resources: 20% at 100% destruction, capped by Sol Hall level.
 *  Attackers at Sol Hall 1-2 get 150% more (x2.5) so new players can afford their first upgrades and walls. */
const BCOST = [0, 150, 600, 3000, 12000, 40000, 100000, 220000, 450000, 900000, 1500000, 2200000, 3000000, 4000000, 5000000, 6500000, 8000000, 9500000];
export const lootBoost = (attackerCore) => ((attackerCore | 0) >= 1 && (attackerCore | 0) <= 2 ? 2.5 : 1);
export function raidLoot(res, core, pct, started = true, attackerCore = 3) {
  const E = Math.max(1, Math.min(17, core | 0)), cap = BCOST[E] * 1.5, flo = BCOST[E] * 0.25;
  const p = Math.max(0, Math.min(100, pct | 0)), lp = p >= 50 || !started ? p : Math.min(50, p + 5), m = lootBoost(attackerCore);
  const pool = (k, cp, fl) => Math.max(fl, Math.min(cp, Math.max(0, +((res || {})[k]) || 0) * 0.2));
  return { g: Math.floor(pool('g', cap, flo) * lp / 100 * m), e: Math.floor(pool('e', cap, flo) * lp / 100 * m), v: E >= 7 ? Math.floor(pool('v', cap / 45, flo / 45) * lp / 100) : 0 };
}

/** Plausibility checks on a reported raid (the server cannot replay the battle yet). */
export function validRaidReport(r) {
  if (!r || typeof r !== 'object') return 'bad body';
  if (!Number.isInteger(r.stars) || r.stars < 0 || r.stars > 3) return 'bad stars';
  if (!Number.isInteger(r.pct) || r.pct < 0 || r.pct > 100) return 'bad pct';
  if (r.pct >= 100 && r.stars < 2) return 'inconsistent';
  if (r.stars >= 1 && r.pct < 50 && !r.coreDestroyed) return 'inconsistent';
  if (r.stars === 3 && r.pct < 100) return 'inconsistent';
  return '';
}
export const RAID_COOLDOWN_MS = 30e3, SHIELD_MS = 4 * 3600e3;

/** Anti-cheat for raids (real players, and practice bases with defender 'practice'; the server cannot replay battles yet):
 *  - the raid must have a ticket from raid-start for this defender, at least MIN_RAID_MS old (a real battle takes time)
 *    and at most MAX_TICKET_MS old;
 *  - trophies won from raids are capped per day (UTC), so faked wins cannot run up the leaderboard. */
export const MIN_RAID_MS = 10e3, /* 10 s: a strong army can wipe a small base fast */ MAX_TICKET_MS = 10 * 60e3, DAILY_TROPHY_CAP = 400;
export function ticketProblem(tk, ticketId, defender, now) {
  if (!tk || !ticketId || tk.id !== ticketId) return 'no raid ticket';
  if (tk.defender !== defender) return 'ticket is for another player';
  const age = now - (+tk.t || 0);
  if (age < MIN_RAID_MS) return 'raid too short';
  if (age > MAX_TICKET_MS) return 'raid ticket expired';
  return '';
}
/** Attacker's trophy change after the daily cap: losses always count, wins only up to what is left of today's cap. */
export const capWin = (delta, wonToday) => (delta <= 0 ? delta : Math.max(0, Math.min(delta, DAILY_TROPHY_CAP - Math.max(0, wonToday))));


/** Token payments in a parsed Solana transaction (getTransaction, encoding jsonParsed): tokens of `mint` moved into a
 *  token account owned by `treasury`, summed per sending wallet → { wallet: tokens }. Used by credit-scan. */
const TOKEN_PROGRAMS = ['TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA', 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb'];
export function paymentsIn(tx, mint, treasury) {
  const keys = (tx?.transaction?.message?.accountKeys ?? []).map((k) => (typeof k === 'string' ? k : k.pubkey));
  const acct = {};
  for (const b of tx?.meta?.postTokenBalances ?? []) acct[keys[b.accountIndex]] = { owner: b.owner, mint: b.mint, decimals: b.uiTokenAmount?.decimals ?? 6 };
  const ixs = [...(tx?.transaction?.message?.instructions ?? []), ...((tx?.meta?.innerInstructions ?? []).flatMap((x) => x.instructions))];
  const out = {};
  for (const ix of ixs) {
    const p = ix?.parsed; if (!p || !TOKEN_PROGRAMS.includes(ix.programId)) continue;
    if (p.type !== 'transferChecked' && p.type !== 'transfer') continue;
    const d = acct[p.info?.destination]; if (!d || d.owner !== treasury || d.mint !== mint) continue;
    if (p.type === 'transferChecked' && p.info?.mint !== mint) continue;
    const from = p.info?.authority ?? p.info?.multisigAuthority; if (!from) continue;
    const amt = p.type === 'transferChecked' ? Number(p.info.tokenAmount?.uiAmount ?? 0) : Number(p.info.amount ?? 0) / 10 ** d.decimals;
    out[from] = (out[from] ?? 0) + amt;
  }
  return out;
}

/** Gems for burning tokens (same packs as src/config.js). */
export const TOKENS_PER_GEM = 1000;
export const GEM_PACKS = [{ gems: 100, off: .5 }, { gems: 500, off: .5 }, { gems: 1200, off: .5 }, { gems: 3000, off: .6 }];
/** Gems for a payment the treasury scan finds: only an exact pack price counts (the game always pays exactly that), so
 *  other transfers into the treasury wallet — e.g. the dev moving tokens — never turn into gems. */
export function gemsForPayment(tokens) {
  for (const p of GEM_PACKS) if (Math.abs(tokens - Math.round(p.gems * TOKENS_PER_GEM * (1 - p.off))) < 1e-3) return p.gems;
  return 0;
}
/** Gift purchases carry `gift:<wallet>` in the transaction's memo: the gems go to that wallet's player instead of the
 *  payer. Returns the recipient wallet, or null for a normal purchase (or a "gift" to yourself). */
const MEMO_PROGRAMS = ['MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr', 'Memo1UhkJRfHyvLMcVucJwxXeuD728EqVDDwQDxFMNo'];
export function giftTarget(tx, payer) {
  for (const ix of tx?.transaction?.message?.instructions ?? []) {
    if (!MEMO_PROGRAMS.includes(ix?.programId)) continue;
    const m = /gift:([1-9A-HJ-NP-Za-km-z]{32,44})/.exec(typeof ix.parsed === 'string' ? ix.parsed : '');
    if (m && m[1] !== payer) return m[1];
  }
  return null;
}
export function gemsForBurn(tokens) {
  let best = 0; for (const p of GEM_PACKS) { const price = Math.round(p.gems * TOKENS_PER_GEM * (1 - p.off)); if (tokens + 1e-9 >= price) best = Math.max(best, p.gems); }
  return best;
}

/** Store item prices in gems (the server charges these; the game shows the same numbers from src/cosmetics.js). */
export const ITEM_PRICES = { firecape: 2500, goldbow: 1000, frostduke: 5000, goldking: 5000, wizard: 5000, firequeen: 5000 };
/** Most copies of an item one player may own (each copy is worn by one hero: the Fire Cape fits 4 heroes). */
export const ITEM_MAX = { firecape: 4, goldbow: 1, frostduke: 1, goldking: 1, wizard: 1, firequeen: 1 };

/** Weekly Top 5 rewards: a week starts Monday 00:00 UTC. Rewards by rank (gems, builder potions). */
export const WEEKLY_REWARDS = [{ gems: 1500, potions: 3 }, { gems: 1000, potions: 2 }, { gems: 500, potions: 1 }, { gems: 500, potions: 0 }, { gems: 250, potions: 0 }];
export function weekStart(ms) { const d = new Date(ms), day = (d.getUTCDay() + 6) % 7; return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day); }
/** The Monday (YYYY-MM-DD) that started the week before the current one: the week whose Top 5 gets rewarded. */
export const lastWeekDay = (ms) => new Date(weekStart(ms) - 7 * 864e5).toISOString().slice(0, 10);

/* ----- Community leaderboard: confirmed share posts (report-share) ----- */
// 48-hour rounds: points count per round (reset each round). The Top 5 of a finished round win gems (SHARE_GEMS, by rank),
// granted by the server (share_settle) once the round's views are final; rounds before SHARE_GEMS_FROM split creator fees in SOL.
export const SHARE_ROUND_MS = 48 * 3600e3, SHARE_EPOCH = Date.UTC(2026, 9, 23, 22); // rounds restarted at publish: 23 Oct 2026 22:00 UTC, then every 48 h
export const SHARE_SPLIT = [0.4, 0.2, 0.2, 0.1, 0.1]; // old SOL rounds: share of the fees by rank
export const SHARE_GEMS = [1000, 750, 500, 250, 250], SHARE_GEMS_FROM = Date.UTC(2026, 9, 31, 22); // keep in step with share_settle (migration 20261107000000)
// views of a finished round are still re-read for SHARE_LATE_MS (report-share), so its prizes are settled after that
export const SHARE_LATE_MS = 6 * 3600e3;
/** Start of the latest round whose prizes can be settled at ms (ended at least SHARE_LATE_MS ago). */
export const shareSettleRound = (ms) => shareRound(ms - SHARE_LATE_MS) - SHARE_ROUND_MS;
// A prize needs at least SHARE_MIN_POINTS in the round, even inside the Top 5 (rounds from 25 Oct 2026 22:00 UTC on).
export const SHARE_MIN_POINTS = 10, SHARE_MIN_FROM = Date.UTC(2026, 9, 25, 22);
export const sharePrizeOk = (points, roundStart) => roundStart < SHARE_MIN_FROM || (points | 0) >= SHARE_MIN_POINTS;
/** Start (ms) of the round that contains ms. */
export const shareRound = (ms) => SHARE_EPOCH + Math.floor((ms - SHARE_EPOCH) / SHARE_ROUND_MS) * SHARE_ROUND_MS;
/** An X or TikTok post link → {platform:'x'|'tt', user, id} | {short:true} (TikTok short link, resolve first) | null. */
export function parsePostUrl(raw) {
  let u; try { u = new URL(String(raw || '').trim()); } catch (e) { return null; }
  if (!/^https?:$/.test(u.protocol) || String(raw).length > 300) return null;
  const host = u.hostname.toLowerCase().replace(/^(www|mobile|m)\./, '');
  if (host === 'x.com' || host === 'twitter.com') { const m = /^\/([A-Za-z0-9_]{1,15})\/status(?:es)?\/(\d{5,25})(?:\/|$)/.exec(u.pathname); return m ? { platform: 'x', user: m[1], id: m[2] } : null; }
  if (host === 'tiktok.com') { const m = /^\/@([\w.]{1,40})\/video\/(\d{5,25})(?:\/|$)/.exec(u.pathname); if (m) return { platform: 'tt', user: m[1], id: m[2] }; return /^\/t\/\w+/.test(u.pathname) ? { short: true } : null; }
  if (host === 'vm.tiktok.com' || host === 'vt.tiktok.com') return /^\/\w+/.test(u.pathname) ? { short: true } : null;
  return null;
}
// Reach: a post earns points by its views (re-read about hourly), its author needs SHARE_MIN_FOLLOWERS followers.
export const SHARE_MIN_FOLLOWERS = 50, SHARE_RESCAN_MS = 50 * 60e3;
export const REACH_TIERS = [[2000, 7], [1000, 6], [500, 5], [400, 4], [300, 3], [200, 2], [0, 1]]; // [views at least, points]
export const reachPoints = (views) => REACH_TIERS.find(([v]) => (Number(views) || 0) >= v)[1];
/** When an X post was made (ms): X post IDs carry their creation time (snowflake: id >> 22 + X's epoch). */
export const xPostTime = (id) => { try { return Number(BigInt(String(id)) >> 22n) + 1288834974657; } catch (e) { return 0; } };
/** Why an X post can't count for the round that started at roundStart (null = it counts). text = the post's own <p>…</p> from oEmbed.
 *  It must mention the game (solclans.com, #SolClans or "Sol Clans" — the CA is optional), carry a video or image (media:
 *  true from FxTwitter, or a pic.twitter.com / pic.x.com link in the text) and be posted during the current round.
 *  (X's embed shows links only as t.co short links, so pass FxTwitter's full text too when there is one.) */
export function xPostProblem(text, id, roundStart, now, media) {
  const plain = String(text || '').replace(/<[^>]*>/g, ' ');
  if (!/solclans\.com|sol\s*clans/i.test(plain)) return 'no link';
  if (!media && !/pic\.(twitter|x)\.com\//i.test(plain)) return 'no media';
  const at = xPostTime(id);
  if (!(at >= roundStart) || at > now + 5 * 60e3) return 'old post';
  return null;
}
/** The post's text (from oEmbed: tweet HTML or TikTok caption) mentions the game. */
export const mentionsSolClans = (text) => /sol\s*clans/i.test(String(text || '').replace(/<[^>]*>/g, ' '));
